import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { STALE_TIME } from 'infrastructure/consts/stale-time';
import { apiUrlGenerator } from 'utils/useTable';
import { CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE } from 'constants/clinicCareManagers';

import useApi from '../use-api';
import { CARE_MANAGERS_QUERY_KEY } from './use-clinic-care-managers';

import type {
  IClinicCareManager,
  IClinicCareManagerForm,
} from 'infrastructure/interfaces';

const ENDPOINT_URL = 'admins/admin-panel/clinics/care-managers';

export type PreparedSelectCareManagersItems = {
  label: string;
  value: string;
};

const CARE_MANAGERS_SELECT_QUERY_KEY = 'careManagersSelectBox';

const clinicCareManagersGetApi = (
  page: number,
  items: number = CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE,
  clinicGuid?: string,
) =>
  apiUrlGenerator(ENDPOINT_URL, items, page, {
    clinicGuid,
  });

export const getClinicCareManagersGetApiQueryKey = (
  page: number,
  items: number,
  clinicGuid?: string,
  labelValueResponse?: boolean,
) => [
  CARE_MANAGERS_SELECT_QUERY_KEY,
  clinicGuid,
  page,
  items,
  labelValueResponse,
];

interface IResponse {
  count: number;
  items: IClinicCareManager[];
}

interface IUseClinicCareManagersProps {
  clinicGuid?: string;
  page: number;
  items?: number;
}

export const useClinicCareManagersSelectBox = (
  props: IUseClinicCareManagersProps,
) => {
  const {
    clinicGuid,
    items = CLINIC_CARE_MANAGERS_TABLE_ITEMS_PER_PAGE,
    page,
  } = props;
  const { loadData, updateData } = useApi();
  const queryClient = useQueryClient();

  const queryKey = getClinicCareManagersGetApiQueryKey(page, items, clinicGuid);

  const {
    data: careManagers = [],
    isFetching: loading,
    refetch,
  } = useQuery({
    queryKey,
    queryFn: async () => {
      const res = await loadData<IResponse>(
        clinicCareManagersGetApi(page, items, clinicGuid),
      );
      return res?.items ?? [];
    },
    enabled: Boolean(clinicGuid),
    staleTime: STALE_TIME,
  });

  const { mutateAsync, isLoading } = useMutation({
    mutationFn: (body: Omit<IClinicCareManagerForm, 'clinicGuid'>) => {
      return updateData(ENDPOINT_URL, {
        ...body,
        clinicGuid,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [CARE_MANAGERS_SELECT_QUERY_KEY, clinicGuid],
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: [CARE_MANAGERS_QUERY_KEY, clinicGuid],
        exact: false,
      });
    },
  });

  return {
    careManagers,
    refetch,
    mutateAsync,
    loading,
    mutateLoading: isLoading,
  };
};
